import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '@/contexts/AuthContext';
import showToast from '@/components/ui/Toast';
import { Dialog, DialogContent, DialogTitle } from '@/components/shadcn/dialog';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

interface CommunityMenuLogoutProps {
  variant: 'mobile' | 'desktop';
}

export function CommunityMenuLogout({ variant }: CommunityMenuLogoutProps) {
  const navigate = useNavigate();
  const { logout, exitCommunity, isCoHost, user } = useAuth();
  const [dialog, setDialog] = useState<'cohost' | 'account' | null>(null);
  const [loading, setLoading] = useState(false);

  const communityName =
    (typeof localStorage !== 'undefined' ? localStorage.getItem('communityName') : '') ||
    user?.community_user?.name ||
    user?.name ||
    'komunitas ini';

  const handleOwnerLogout = async () => {
    try {
      await logout();
    } catch {
      navigate('/login');
    }
  };

  const handleExitCoHost = async () => {
    setLoading(true);
    try {
      await exitCommunity();
      setDialog(null);
      navigate('/', { replace: true });
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Gagal keluar dari mode co-host'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAccount = async () => {
    setLoading(true);
    try {
      await logout();
    } finally {
      setLoading(false);
      setDialog(null);
    }
  };

  if (!isCoHost) {
    if (variant === 'desktop') {
      return (
        <button
          type="button"
          onClick={() => { void handleOwnerLogout(); }}
          className="px-5 py-2.5 rounded-full border border-red-200 text-red-500 font-bold text-xs flex items-center gap-2 hover:bg-red-50 transition-all bg-white cursor-pointer shadow-xs active:scale-95"
        >
          <Icon icon="ph:sign-out-bold" className="w-4 h-4" />
          <span>Keluar Akun</span>
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => { void handleOwnerLogout(); }}
        className="w-full py-3 px-4 rounded-full border border-red-200 text-red-500 font-extrabold text-sm flex items-center justify-center gap-2 hover:bg-red-50 active:scale-98 transition-all bg-white cursor-pointer shadow-2xs mt-4"
      >
        <Icon icon="ph:sign-out-bold" className="w-4 h-4" />
        <span>Logout</span>
      </button>
    );
  }

  return (
    <>
      <div className={variant === 'desktop' ? 'flex items-center gap-2' : 'space-y-2.5 mt-4'}>
        <button
          type="button"
          onClick={() => setDialog('cohost')}
          className={
            variant === 'desktop'
              ? 'px-4 py-2.5 rounded-full border border-orange-200 text-[#FC480D] font-bold text-xs flex items-center gap-2 hover:bg-orange-50 bg-white cursor-pointer'
              : 'w-full py-3 px-4 rounded-full border border-orange-200 text-[#FC480D] font-extrabold text-sm flex items-center justify-center gap-2 hover:bg-orange-50 bg-white cursor-pointer shadow-2xs'
          }
        >
          <Icon icon="ph:arrow-u-up-left-bold" className="w-4 h-4" />
          Logout Co-host
        </button>
        <button
          type="button"
          onClick={() => setDialog('account')}
          className={
            variant === 'desktop'
              ? 'px-4 py-2.5 rounded-full border border-red-200 text-red-500 font-bold text-xs flex items-center gap-2 hover:bg-red-50 bg-white cursor-pointer'
              : 'w-full py-3 px-4 rounded-full border border-red-200 text-red-500 font-extrabold text-sm flex items-center justify-center gap-2 hover:bg-red-50 bg-white cursor-pointer shadow-2xs'
          }
        >
          <Icon icon="ph:sign-out-bold" className="w-4 h-4" />
          Logout Akun
        </button>
      </div>

      <Dialog open={dialog === 'cohost'} onOpenChange={(open) => { if (!open && !loading) setDialog(null); }}>
        <DialogContent showCloseButton={false} className="max-w-sm rounded-3xl p-6 bg-white text-center gap-0">
          <DialogTitle className="text-base font-extrabold text-[#0E1726] mb-2">Logout Co-host</DialogTitle>
          <p className="text-sm text-gray-400 font-medium">
            Keluar dari {communityName} sebagai co-host? Akunmu tetap login.
          </p>
          <div className="mt-5 flex flex-col gap-2.5">
            <button
              type="button"
              disabled={loading}
              onClick={() => { void handleExitCoHost(); }}
              className="w-full h-11 rounded-full bg-[#FC480D] text-white text-sm font-extrabold cursor-pointer disabled:opacity-60"
            >
              {loading ? 'Memproses...' : 'Ya, keluar co-host'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => setDialog(null)}
              className="w-full h-11 rounded-full bg-gray-100 text-gray-700 text-sm font-bold cursor-pointer"
            >
              Batal
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === 'account'} onOpenChange={(open) => { if (!open && !loading) setDialog(null); }}>
        <DialogContent showCloseButton={false} className="max-w-sm rounded-3xl p-6 bg-white text-center gap-0">
          <DialogTitle className="text-base font-extrabold text-[#0E1726] mb-2">Logout Akun</DialogTitle>
          <p className="text-sm text-gray-400 font-medium">
            Logout dari {communityName} dan akun Sporto?
          </p>
          <div className="mt-5 flex flex-col gap-2.5">
            <button
              type="button"
              disabled={loading}
              onClick={() => { void handleLogoutAccount(); }}
              className="w-full h-11 rounded-full bg-red-500 text-white text-sm font-extrabold cursor-pointer disabled:opacity-60"
            >
              {loading ? 'Memproses...' : 'Ya, logout'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => setDialog(null)}
              className="w-full h-11 rounded-full bg-gray-100 text-gray-700 text-sm font-bold cursor-pointer"
            >
              Batal
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
